import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeOff, Flag, Globe2, Heart, MessageSquare, MessageSquareOff, MoreHorizontal, Pin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { StatusBadge } from "@/components/common/States";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { ADMIN_ROLES, useSchoolCan } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { FEED_KIND, ROLE_LABELS, timeAgo } from "@/lib/labels";
import { CommentThread } from "./CommentThread";
import { LinkCard } from "./LinkCard";

const Inisial = ({ nama }) => (
  <span className="inline-flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-[#0B3D91] text-xs sm:text-sm font-bold text-white dark:bg-[#3B82F6]" aria-hidden>
    {(nama || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
  </span>
);

export function PostCard({ post, ctx, onReport, onRefresh }) {
  const bisaModerasi = useSchoolCan("feed:moderate");
  const pengurus = ADMIN_ROLES.includes(ctx.role);
  const bolehUbah = post.mine || pengurus;
  const bolehSemat = bolehUbah && post.audience === "all" && pengurus;
  const bolehPublik = bolehUbah && post.audience === "all" && ctx.settings.public_page_enabled;
  const bolehModerasi = ctx.can_moderate && bisaModerasi;
  const [bukaKomentar, setBukaKomentar] = useState(false);
  const [suka, setSuka] = useState(null);
  const [tarik, setTarik] = useState(false);

  const liked = suka?.liked ?? post.liked;
  const likeCount = suka?.like_count ?? post.like_count;

  const aksi = useMutation({
    mutationFn: async ({ url, body }) => (await api.post(url, body)).data,
    onSuccess: onRefresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const sukaMutasi = useMutation({
    mutationFn: async () => (await api.post(`/school/feed/posts/${post.id}/like`)).data,
    onSuccess: (d) => setSuka(d),
    onError: (e) => toast.error(errorMessage(e)),
  });

  const ubah = (changes, pesan) => aksi.mutate(
    { url: `/school/feed/posts/${post.id}/settings`, body: changes },
    { onSuccess: () => toast.success(pesan) },
  );

  return (
    <article className={`rounded-xl border bg-card ${post.status === "hidden" ? "border-dashed border-amber-300 dark:border-amber-800" : ""}`} data-testid="feed-post">
      {/* Header Row 1: Badges + menu */}
      <div className="flex items-center justify-between px-3 sm:px-3.5 pt-2.5 sm:pt-3 pb-1 sm:pb-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {post.pinned && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-[#0B3D91] dark:bg-blue-950 dark:text-blue-200" data-testid="post-pinned-badge">
              <Pin className="h-3 w-3" aria-hidden /> Disematkan
            </span>
          )}
          <StatusBadge map={FEED_KIND} value={post.kind} testId="post-kind-badge" />
        </div>
        {(bolehUbah || bolehModerasi || !post.mine) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Tindakan postingan" data-testid="post-menu-button">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="post-menu">
              {bolehUbah && (
                <DropdownMenuItem disabled={aksi.isPending} data-testid="post-toggle-comments"
                  onClick={() => ubah({ comments_enabled: !post.comments_enabled },
                    post.comments_enabled ? "Komentar dimatikan." : "Komentar diizinkan kembali.")}>
                  {post.comments_enabled ? <MessageSquareOff className="mr-2 h-4 w-4" /> : <MessageSquare className="mr-2 h-4 w-4" />}
                  {post.comments_enabled ? "Matikan komentar" : "Izinkan komentar"}
                </DropdownMenuItem>
              )}
              {bolehSemat && (
                <DropdownMenuItem disabled={aksi.isPending} data-testid="post-toggle-pin"
                  onClick={() => ubah({ pinned: !post.pinned }, post.pinned ? "Sematan dilepas." : "Postingan disematkan.")}>
                  <Pin className="mr-2 h-4 w-4" /> {post.pinned ? "Lepas sematan" : "Sematkan di atas"}
                </DropdownMenuItem>
              )}
              {bolehPublik && (
                <DropdownMenuItem disabled={aksi.isPending} data-testid="post-toggle-public"
                  onClick={() => ubah({ public: !post.public }, post.public ? "Dihapus dari halaman publik." : "Ditampilkan di halaman publik.")}>
                  <Globe2 className="mr-2 h-4 w-4" /> {post.public ? "Hapus dari halaman publik" : "Tampilkan di halaman publik"}
                </DropdownMenuItem>
              )}
              {bolehModerasi && (
                <DropdownMenuItem disabled={aksi.isPending} data-testid="post-toggle-hide"
                  onClick={() => (post.status === "hidden"
                    ? aksi.mutate({ url: `/school/feed/posts/${post.id}/restore` }, { onSuccess: () => toast.success("Postingan ditayangkan kembali.") })
                    : setTarik(true))}>
                  {post.status === "hidden" ? <Eye className="mr-2 h-4 w-4" /> : <EyeOff className="mr-2 h-4 w-4" />}
                  {post.status === "hidden" ? "Tayangkan kembali" : "Tarik dari feed"}
                </DropdownMenuItem>
              )}
              {!post.mine && (
                <>
                  {(bolehUbah || bolehModerasi) && <DropdownMenuSeparator />}
                  <DropdownMenuItem className="text-[#E11D2E] focus:text-[#E11D2E]" data-testid="post-report-button"
                    onClick={() => onReport({ type: "post", id: post.id })}>
                    <Flag className="mr-2 h-4 w-4" /> Laporkan konten
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Header Row 2: Avatar + author info */}
      <div className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-3.5 pb-2 sm:pb-2.5">
        <Inisial nama={post.author?.full_name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm sm:text-[15px] font-semibold leading-tight" data-testid="post-author">{post.author?.full_name || "Anggota sekolah"}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
            <span>{ROLE_LABELS[post.author?.role] || post.author?.role}</span>
            <span aria-hidden>·</span>
            <time dateTime={post.created_at} data-testid="post-time">{timeAgo(post.created_at)}</time>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1" data-testid="post-audience">
              {post.audience === "all" ? <><Globe2 className="h-3 w-3" aria-hidden /> Seluruh sekolah</>
                : <><Users className="h-3 w-3" aria-hidden /> {post.class_names?.join(", ") || "Kelas tertentu"}</>}
            </span>
          </div>
        </div>
      </div>

      {post.status === "hidden" && (
        <p className="mx-3 sm:mx-3.5 mb-2 sm:mb-2.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="post-hidden-banner">
          Postingan ini ditarik moderator dan hanya terlihat oleh moderator.{post.hidden_reason && <> Alasan: {post.hidden_reason}</>}
        </p>
      )}

      {/* Body */}
      <div className="px-3 sm:px-3.5 pb-2 sm:pb-2.5">
        <p className="whitespace-pre-wrap break-words text-sm sm:text-[15px] leading-relaxed" data-testid="post-body">{post.body}</p>
      </div>

      {post.links?.length > 0 && (
        <div className="grid gap-2 px-3 sm:px-3.5 pb-2 sm:pb-2.5 sm:grid-cols-2">
          {post.links.map((l, i) => <LinkCard key={`${l.url}-${i}`} link={l} testId={`post-link-${i}`} />)}
        </div>
      )}

      <Separator />

      {/* Action bar */}
      <div className="flex items-center gap-0.5 sm:gap-1 px-1 sm:px-1.5 py-0.5 sm:py-1">
        <Button variant="ghost" size="sm" className="flex-1 gap-1 sm:gap-1.5 h-8 sm:h-9" aria-pressed={liked} disabled={sukaMutasi.isPending}
          onClick={() => sukaMutasi.mutate()} data-testid="post-like-button">
          <Heart className={`h-3.5 sm:h-4 w-3.5 sm:w-4 ${liked ? "fill-[#E11D2E] text-[#E11D2E]" : ""}`} aria-hidden />
          <span className="text-xs sm:text-sm font-medium">Suka</span>
          {likeCount > 0 && <span className="tabular-nums text-muted-foreground">{likeCount}</span>}
        </Button>
        <Button variant="ghost" size="sm" className="flex-1 gap-1 sm:gap-1.5 h-8 sm:h-9" aria-expanded={bukaKomentar} disabled={post.status === "hidden"}
          onClick={() => setBukaKomentar((b) => !b)} data-testid="post-comments-button">
          <MessageSquare className="h-3.5 sm:h-4 w-3.5 sm:w-4" aria-hidden />
          <span className="text-xs sm:text-sm font-medium">Komentar</span>
          {post.comment_count > 0 && <span className="tabular-nums text-muted-foreground">{post.comment_count}</span>}
        </Button>
      </div>

      {!post.comments_enabled && (
        <p className="px-3 sm:px-4 pb-2 text-right text-[11px] text-muted-foreground" data-testid="post-comments-off">Komentar dimatikan</p>
      )}

      {bukaKomentar && <CommentThread post={post} canModerate={bolehModerasi} onReport={onReport} />}

      <ConfirmReasonDialog open={tarik} onOpenChange={setTarik} danger testId="post-hide-dialog"
        title="Tarik postingan dari feed" confirmLabel="Tarik postingan"
        description="Postingan tidak lagi terlihat warga sekolah, tetapi tetap dapat ditinjau moderator. Tindakan ini tercatat di log audit."
        onConfirm={async (reason) => {
          await api.post(`/school/feed/posts/${post.id}/hide`, { reason });
          toast.success("Postingan ditarik dari feed.");
          setTarik(false);
          onRefresh();
        }} />
    </article>
  );
}
